import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Settings,
  Activity,
  TrendingUp,
  Search,
  RefreshCw,
  Clock,
  Shield,
  Database,
  Zap,
  Plus,
  Grid3X3,
  List,
  ChevronRight,
  UserCheck,
  Layers,
  Plug,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { useToast } from "../components/ui/Toast";
import { useUsersApi } from "../apis/users-apis";
import SageMakerLogo from "../assets/sagemaker.png";

// Types
type TimeRange = "24h" | "7d" | "30d";
type ViewMode = "grid" | "list";

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  coreItems: number;
  systemHealth: number;
  userGrowth: string;
}

interface ActivityItem {
  id: string;
  type: "user" | "system" | "security" | "admin";
  title: string;
  description: string;
  timestamp: string;
  user?: string;
}

export const AdminLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { getUsers } = useUsersApi();

  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange>("24h");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    activeUsers: 0,
    coreItems: 0,
    systemHealth: 99.9,
    userGrowth: "+12.5%",
  });
  const [activities, setActivities] = useState<ActivityItem[]>([]);

  // Fetch data
  const fetchAdminData = async () => {
    try {
      const users = await getUsers();

      if (users && Array.isArray(users)) {
        setStats((prev) => ({
          ...prev,
          totalUsers: users.length,
          activeUsers:
            users.filter((user: any) => user.isActive === true).length ||
            Math.floor(users.length * 0.89),
        }));
      }

      // Mock activity data
      setActivities([
        {
          id: "1",
          type: "user",
          title: "New user registered",
          description: "john.doe@company.com joined the platform",
          timestamp: "2 minutes ago",
          user: "John Doe",
        },
        {
          id: "2",
          type: "system",
          title: "System backup completed",
          description: "Daily backup completed successfully",
          timestamp: "1 hour ago",
        },
        {
          id: "3",
          type: "security",
          title: "Security scan completed",
          description: "No vulnerabilities detected",
          timestamp: "3 hours ago",
        },
        {
          id: "4",
          type: "admin",
          title: "Core item updated",
          description: "AI Assistant Agent configuration updated",
          timestamp: "5 hours ago",
          user: "Admin",
        },
      ]);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      addToast({
        title: "Error",
        description: "Failed to load admin data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, [timeRange]);

  const handleRedirect = () => {
    window.open(process.env.REACT_APP_SAGEMAKER_ENDPOINT, "_blank");
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAdminData();
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "user":
        return <Users className="h-4 w-4" />;
      case "system":
        return <Settings className="h-4 w-4" />;
      case "security":
        return <Shield className="h-4 w-4" />;
      case "admin":
        return <Database className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case "user":
        return "bg-blue-500";
      case "system":
        return "bg-green-500";
      case "security":
        return "bg-orange-500";
      case "admin":
        return "bg-purple-500";
      default:
        return "bg-gray-500";
    }
  };

  const filteredActivities = activities.filter(
    (activity) =>
      activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="w-8 h-8 text-blue-500" />
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl font-['Poppins','SF_Pro_Display','system-ui','-apple-system','sans-serif'] antialiased">
      {/* Hero Header Section */}
      <div className="relative mb-8 overflow-hidden">
        {/* Multi-layer gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-800"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/30 via-transparent to-indigo-500/30"></div>

        {/* Animated background elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-400/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-purple-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>

        {/* Glassmorphism overlay */}
        <div className="absolute inset-0 backdrop-blur-sm bg-gradient-to-r from-white/10 to-transparent"></div>

        {/* Content */}
        <div className="relative z-10 px-4 sm:px-8 py-8 sm:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-2xl shadow-lg self-start">
                  <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-2 drop-shadow-lg">
                    Admin Dashboard
                  </h1>
                  <p className="text-purple-100 text-base sm:text-lg font-medium max-w-2xl leading-relaxed">
                    Monitor system health, manage users, and oversee core
                    platform components with comprehensive administrative
                    controls
                  </p>
                </div>
              </div>

              {/* Live Stats */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-6">
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-white">
                      {stats.totalUsers}
                    </span>{" "}
                    Total Users
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-green-200">
                      {stats.activeUsers}
                    </span>{" "}
                    Active Users
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-indigo-200">
                      86
                    </span>{" "}
                    Core Items
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-emerald-200">
                      {stats.systemHealth}%
                    </span>{" "}
                    Uptime
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 flex-col justify-between md:h-44">
              <div>
                <Button
                  onClick={() => handleRedirect()}
                  className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 hover:border-white/50 p-2 rounded-lg"
                >
                  <img
                    src={SageMakerLogo}
                    alt="SageMaker Logo"
                    className="h-6  w-6 mr-2 rounded cursor-pointer"
                  />
                  SageMaker
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value as TimeRange)}
                  className="px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-sm text-white focus:ring-2 focus:ring-white/50 focus:border-white/50"
                >
                  <option value="24h" className="text-gray-900">
                    Last 24h
                  </option>
                  <option value="7d" className="text-gray-900">
                    Last 7d
                  </option>
                  <option value="30d" className="text-gray-900">
                    Last 30d
                  </option>
                </select>
                <Button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 hover:border-white/50 p-2 rounded-lg"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search recent activity..."
              className="pl-10 pr-4 border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center">
          <div className="flex items-center border border-gray-300 rounded-lg bg-white">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 transition-colors ${viewMode === "grid" ? "bg-purple-500 text-white" : "text-gray-400 hover:text-purple-500"}`}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 transition-colors ${viewMode === "list" ? "bg-purple-500 text-white" : "text-gray-400 hover:text-purple-500"}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions Panel */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card
          className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-500 hover:border-l-blue-600"
          onClick={() => navigate("/studio/admin/users")}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl text-white group-hover:scale-110 transition-transform duration-300">
                  <Users className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">
                    Manage Users
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    View, edit, and manage user accounts and permissions
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-purple-500 hover:border-l-purple-600"
          onClick={() => navigate("/studio/admin/core-items")}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl text-white group-hover:scale-110 transition-transform duration-300">
                  <Database className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">
                    Core Items
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    Manage system agents, tools, and platform components
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-purple-500 transition-colors flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-semibold text-gray-900">
              Recent Activity
            </CardTitle>
            <Badge variant="outline" className="text-sm">
              {filteredActivities.length} items
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {filteredActivities.length === 0 ? (
            <div className="text-center py-8">
              <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No recent activity
              </h3>
              <p className="text-gray-600">
                {searchTerm
                  ? "No activities match your search."
                  : "System activity will appear here."}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredActivities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start space-x-4 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <div
                    className={`p-2 rounded-full ${getActivityColor(activity.type)} text-white flex-shrink-0 mt-1`}
                  >
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900 truncate">
                        {activity.title}
                      </h4>
                      <span className="text-sm text-gray-500 flex-shrink-0 ml-2">
                        {activity.timestamp}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {activity.description}
                    </p>
                    {activity.user && (
                      <div className="flex items-center mt-2">
                        <UserCheck className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-500">
                          {activity.user}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLandingPage;
